import { createNavigatorBinding } from 'WebmcpSdk/browser-binding';
import { publishRegistry } from 'WebmcpSdk/registry-bridge';
import { featureDetect, type SurfaceTier, WebMcpRegistry } from '@bomdisoft/webmcp-core';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export interface WebMcpContextValue {
  promptConsent: (input: { toolName: string; description: string; preview: string }) => Promise<boolean>;
  registry: WebMcpRegistry | null;
  routes: Readonly<Record<string, string>>;
  surfaceTier: SurfaceTier;
  tenantId: string | null;
}

const WebMcpContext = createContext<WebMcpContextValue | null>(null);

export interface WebMcpProviderProps {
  children: ReactNode;
  promptConsent?: (input: { toolName: string; description: string; preview: string }) => Promise<boolean>;
  routes?: Readonly<Record<string, string>>;
  surfaceTier: SurfaceTier;
  tenantId?: string;
}

export function WebMcpProvider(props: WebMcpProviderProps) {
  const enabled = featureDetect();
  const [registry] = useState<WebMcpRegistry | null>(() => {
    if (!enabled) return null;
    return new WebMcpRegistry({
      binding: createNavigatorBinding(),
      prompt: props.promptConsent ?? (async () => false),
      surface: props.surfaceTier,
    });
  });

  // Publishing the registry outside the tree is what lets a tool host mount as a
  // sibling of the provider rather than a descendant — see registry-bridge.
  useEffect(() => {
    publishRegistry(registry);
    return () => {
      publishRegistry(null);
      registry?.disposeAll();
    };
  }, [registry]);

  const value = useMemo<WebMcpContextValue>(
    () => ({
      promptConsent: props.promptConsent ?? (async () => false),
      registry,
      routes: props.routes ?? {},
      surfaceTier: props.surfaceTier,
      tenantId: props.tenantId ?? null,
    }),
    [registry, props.surfaceTier, props.tenantId, props.routes, props.promptConsent],
  );

  return <WebMcpContext.Provider value={value}>{props.children}</WebMcpContext.Provider>;
}

export function useWebMcpContext(): WebMcpContextValue | null {
  return useContext(WebMcpContext);
}
